import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import DashboardEmptyState from "../components/DashboardEmptyState";
import { dashboardTheme } from "../theme/dashboardTheme";
import { IMarketIndexMonth } from "../types/ownerDashboard.types";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

interface MarketIndexChartProps {
  title: string;
  indexLabel: string;
  monthlyHistory: IMarketIndexMonth[];
  isUnavailable?: boolean;
  isLoading?: boolean;
}

export default function MarketIndexChart({
  title,
  indexLabel,
  monthlyHistory,
  isUnavailable = false,
  isLoading = false,
}: MarketIndexChartProps) {
  const sortedHistory = useMemo(
    () =>
      [...monthlyHistory].sort((first, second) => {
        const [monthA, yearA] = first.month.split("/");
        const [monthB, yearB] = second.month.split("/");
        return (
          new Date(`${yearA}-${monthA}-01`).getTime() -
          new Date(`${yearB}-${monthB}-01`).getTime()
        );
      }),
    [monthlyHistory]
  );

  const chartData = useMemo(
    () => ({
      labels: sortedHistory.map((entry) => entry.month),
      datasets: [
        {
          data: sortedHistory.map((entry) => entry.value_pct),
          backgroundColor: sortedHistory.map((entry) =>
            entry.value_pct >= 0 ? "rgba(63, 185, 80, 0.75)" : "rgba(248, 81, 73, 0.75)"
          ),
          borderRadius: 4,
          maxBarThickness: 18,
        },
      ],
    }),
    [sortedHistory]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: dashboardTheme.colors.surfaceElevated,
          borderColor: dashboardTheme.colors.cardBorder,
          borderWidth: 1,
          titleColor: dashboardTheme.colors.textPrimary,
          bodyColor: dashboardTheme.colors.textSecondary,
          callbacks: {
            label: (context: { parsed: { y: number | null } }) =>
              `${indexLabel}: ${context.parsed.y?.toFixed(2) ?? "0.00"}%`,
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: dashboardTheme.colors.chartTick,
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 6,
            font: { size: 10 },
          },
        },
        y: {
          grid: { color: dashboardTheme.colors.chartGrid },
          ticks: {
            color: dashboardTheme.colors.chartTick,
            callback: (value: string | number) => `${value}%`,
            font: { size: 10 },
          },
        },
      },
    }),
    [indexLabel]
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-xs font-semibold text-[#8b949e]">{title}</p>
        <div className="h-36 w-full animate-pulse rounded-2xl bg-white/[0.06] sm:h-40" aria-hidden />
      </div>
    );
  }

  if (isUnavailable) {
    return (
      <DashboardEmptyState
        align="center"
        title={`Gráfico do ${indexLabel} indisponível`}
        description="Não foi possível carregar o índice neste momento."
        className="flex h-36 items-center justify-center"
      />
    );
  }

  if (sortedHistory.length === 0) {
    return (
      <DashboardEmptyState
        align="center"
        title={`Histórico do ${indexLabel} indisponível`}
        description="Não há dados de referência para exibir o gráfico neste momento."
        className="flex h-36 items-center justify-center"
      />
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <p className="text-xs font-semibold text-[#8b949e]">{title}</p>
      <div className="h-36 w-full min-w-0 sm:h-40">
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
}
